import React, { useState, useCallback } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Spin } from "antd"
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core"
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import EquipmentCard from "../EquipmentCard"
import { restrictToParentElement } from "ui/SortableTableRow/restrictToParentElement"
import styles from "./index.module.css"

const INITIAL_DISPLAY_COUNT = 10

const SortableEquipmentCard = ({ equipment, onDeactivate, onActivate, onDelete, onAssignClick, dragDisabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(equipment.key ?? equipment.id),
    disabled: dragDisabled
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  }

  return (
    <div ref={setNodeRef} style={style}>
      <EquipmentCard
        equipment={equipment}
        onDeactivate={onDeactivate}
        onActivate={onActivate}
        onDelete={onDelete}
        onAssignClick={onAssignClick}
        dragHandleProps={dragDisabled ? null : { ...listeners, ...attributes }}
        dragDisabled={dragDisabled}
      />
    </div>
  )
}

SortableEquipmentCard.propTypes = {
  equipment: PropTypes.object.isRequired,
  onDeactivate: PropTypes.func,
  onActivate: PropTypes.func,
  onDelete: PropTypes.func,
  onAssignClick: PropTypes.func,
  dragDisabled: PropTypes.bool
}

const EquipmentCardList = ({
  data,
  onDeactivate,
  onActivate,
  onDelete,
  onAssignClick,
  onReorder,
  dragDisabled,
  loading
}) => {
  const [showAll, setShowAll] = useState(false)
  const [liveIds, setLiveIds] = useState(null)
  const liveIdsRef = React.useRef(null)
  const lastOverIdRef = React.useRef(null)

  const displayData = showAll ? data : data.slice(0, INITIAL_DISPLAY_COUNT)
  const hasMore = data.length > INITIAL_DISPLAY_COUNT

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  )

  const baseIds = displayData.map((item) => String(item.key ?? item.id))
  const sortableIds = liveIds ?? baseIds

  const liveDisplayData = liveIds
    ? liveIds.map((id) => displayData.find((item) => String(item.key ?? item.id) === id)).filter(Boolean)
    : displayData

  const handleDragOver = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return
      if (lastOverIdRef.current === String(over.id)) return
      lastOverIdRef.current = String(over.id)
      const ids = liveIdsRef.current ?? baseIds
      const oldIndex = ids.indexOf(String(active.id))
      const newIndex = ids.indexOf(String(over.id))
      if (oldIndex === -1 || newIndex === -1) return
      const next = arrayMove(ids, oldIndex, newIndex)
      liveIdsRef.current = next
      setLiveIds(next)
    },
    [baseIds]
  )

  const handleDragEnd = useCallback(
    ({ active }) => {
      const finalIds = liveIdsRef.current
      liveIdsRef.current = null
      lastOverIdRef.current = null
      setLiveIds(null)
      if (!finalIds) return
      const activeId = String(active.id)
      const oldIndex = baseIds.indexOf(activeId)
      const newIndex = finalIds.indexOf(activeId)
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return
      const overId = baseIds[newIndex]
      if (!overId) return
      onReorder?.(activeId, overId)
    },
    [onReorder, baseIds]
  )

  const handleDragCancel = useCallback(() => {
    liveIdsRef.current = null
    lastOverIdRef.current = null
    setLiveIds(null)
  }, [])

  return (
    <div className={styles.container}>
      <Spin spinning={Boolean(loading)}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToParentElement]} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <Flex vertical gap={8} className={styles.cardList}>
              {liveDisplayData.map((equipment) => (
                <SortableEquipmentCard
                  key={equipment.key ?? equipment.id}
                  equipment={equipment}
                  onDeactivate={onDeactivate}
                  onActivate={onActivate}
                  onDelete={onDelete}
                  onAssignClick={onAssignClick}
                  dragDisabled={dragDisabled}
                />
              ))}
            </Flex>
          </SortableContext>
        </DndContext>
      </Spin>

      {hasMore && !showAll && (
        <div className={styles.seeMoreContainer}>
          <Button
            type="link"
            onClick={() => setShowAll(true)}
            className={styles.seeMoreButton}
          >
            See More
          </Button>
        </div>
      )}

      {data.length === 0 && (
        <div className={styles.emptyState}>
          <p>No equipment found</p>
        </div>
      )}
    </div>
  )
}

EquipmentCardList.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      image: PropTypes.string,
      is_active: PropTypes.bool,
      category: PropTypes.string,
      asset_count: PropTypes.number,
      assigned_experiences: PropTypes.array,
      upcoming_bookings: PropTypes.number
    })
  ).isRequired,
  onDeactivate: PropTypes.func,
  onActivate: PropTypes.func,
  onDelete: PropTypes.func,
  onAssignClick: PropTypes.func,
  onReorder: PropTypes.func,
  dragDisabled: PropTypes.bool,
  loading: PropTypes.bool
}

export default React.memo(EquipmentCardList)
